import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import cantalLogo from '../assets/cantal.png';

type Step = 'email' | 'password' | 'first-setup';

const input: React.CSSProperties = {
  padding: '0.65rem 0.875rem',
  fontSize: '0.95rem',
  borderRadius: '8px',
  border: '1.5px solid #d1d5db',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  color: '#111827',
  background: '#fff',
};

const primaryBtn = (disabled = false): React.CSSProperties => ({
  padding: '0.7rem 1.2rem',
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  borderRadius: '8px',
  border: 'none',
  background: disabled ? '#93c5fd' : '#2563eb',
  color: '#fff',
  width: '100%',
  letterSpacing: '0.01em',
});

const ghostBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#6b7280',
  fontSize: '0.85rem',
  cursor: 'pointer',
  padding: '0.25rem 0',
};

export default function LoginPage(): JSX.Element {
  const { checkEmail, loginWithEmail, firstSetup, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    void fetch('/api/auth/needs-setup')
      .then(r => r.json())
      .then((d: { needsSetup: boolean }) => setNeedsSetup(d.needsSetup))
      .catch(() => {});
  }, []);

  if (user) { void navigate('/'); return <></>; }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { exists, requiresSetup } = await checkEmail(email);
      if (!exists) {
        setError("Aucun compte associé à cet email. Contactez l'administrateur.");
        return;
      }
      if (requiresSetup) {
        void navigate('/set-password', { state: { email } });
        return;
      }
      setStep('password');
    } catch {
      setError('Erreur réseau. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await loginWithEmail(email, password);
      void navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFirstSetupSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setSubmitting(true);
    try {
      await firstSetup(email, password, displayName);
      void navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(145deg, #e8f0fe 0%, #f0f4ff 60%, #fafbff 100%)',
      padding: '1rem',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(30,58,95,0.10), 0 1px 4px rgba(0,0,0,0.05)',
        padding: '2.5rem 2rem',
        width: '100%',
        maxWidth: '380px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.25rem',
      }}>
        <img src={cantalLogo} alt="Pierroinfo" style={{ height: '72px', width: 'auto' }} />
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: 0, color: '#111827', letterSpacing: '-0.01em' }}>
          Pierroinfo
        </h1>

        {step === 'email' && (
          <form onSubmit={(e) => void handleEmailSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
            <input type="email" placeholder="Votre email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus style={input} />
            {error && <p style={{ color: '#dc2626', margin: 0, fontSize: '0.85rem', background: '#fef2f2', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>{error}</p>}
            <button type="submit" disabled={submitting} style={primaryBtn(submitting)}>
              {submitting ? 'Vérification…' : 'Continuer →'}
            </button>
            {needsSetup && (
              <button type="button" onClick={() => { setStep('first-setup'); setError(''); }} style={{ ...ghostBtn, marginTop: '0.25rem', color: '#9ca3af', fontSize: '0.78rem' }}>
                Première installation
              </button>
            )}
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={(e) => void handleLoginSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.6rem 0.875rem', fontSize: '0.88rem', color: '#374151' }}>
              {email}
            </div>
            <input type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus minLength={8} style={input} />
            {error && <p style={{ color: '#dc2626', margin: 0, fontSize: '0.85rem', background: '#fef2f2', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>{error}</p>}
            <button type="submit" disabled={submitting} style={primaryBtn(submitting)}>
              {submitting ? 'Connexion…' : 'Se connecter'}
            </button>
            <button type="button" onClick={() => { setStep('email'); setPassword(''); setError(''); }} style={ghostBtn}>
              ← Changer d'email
            </button>
          </form>
        )}

        {step === 'first-setup' && (
          <form onSubmit={(e) => void handleFirstSetupSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Créer le compte administrateur</p>
            <input type="text" placeholder="Nom affiché" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required autoFocus style={input} />
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={input} />
            <input type="password" placeholder="Mot de passe (8 car. min.)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} style={input} />
            <input type="password" placeholder="Confirmer le mot de passe" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={input} />
            {error && <p style={{ color: '#dc2626', margin: 0, fontSize: '0.85rem', background: '#fef2f2', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>{error}</p>}
            <button type="submit" disabled={submitting} style={primaryBtn(submitting)}>
              {submitting ? 'Création…' : 'Créer le compte admin'}
            </button>
            <button type="button" onClick={() => { setStep('email'); setError(''); }} style={ghostBtn}>
              ← Retour
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
